import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {assertAllAccessible, isInaccessible} from 'graphql-client/InaccessibleInfo';
import APIUtil from 'util/APIUtil';

import {
  ProcessTask,
  ProcessOutput,
  ProcessAsyncRecord,
  SchemaMapper,
  isAsyncDataStartAutomatedMapping,
  RoutingErrorRecord,
} from './FixFailedRoutingAndMappingConstants';

export function getItemsToMapFromRoutingError(
  asyncRecord: RoutingErrorRecord,
  task: ProcessTask
): Array<ProcessTask> | Array<ProcessOutput> {
  assert(
    asyncRecord.asyncData.blockedMappersAndExpressions.length > 0,
    'Expected at least one router on the path'
  );
  const schemas = assertAllAccessible(
    asyncRecord.asyncData.blockedMappersAndExpressions.map(({schemaMapper}) => {
      assert(schemaMapper.fromSchemas.length === 1, 'Expected routers to block non-merging mappers');
      return schemaMapper.fromSchemas[0];
    })
  );
  assert(
    schemas.every(({id}) => id === schemas[0].id),
    'Expected all blocked mappers to be from the same schema'
  );
  const schemaId = schemas[0].id;
  if (APIUtil.isProcessOutputSchema(schemaId)) {
    return task.outputs;
  }
  return [task];
}

export function getSchemaMappers(asyncRecord: ProcessAsyncRecord): Array<SchemaMapper> {
  return isAsyncDataStartAutomatedMapping(asyncRecord.asyncData)
    ? asyncRecord.asyncData.blockedMappersAndExpressions.map(({schemaMapper}) => schemaMapper)
    : [asyncRecord.asyncData.schemaMapper];
}

// With lazy task group creation, we use schema names instead of node names.
export function getMapperIdToDownstreamSchemaName(asyncRecord: ProcessAsyncRecord): {[id: string]: string} {
  const schemaMappers = getSchemaMappers(asyncRecord);

  return schemaMappers.reduce(
    (acc, schemaMapper) => {
      acc[schemaMapper.id] = isInaccessible(schemaMapper.toSchema)
        ? 'Inaccessible schema'
        : schemaMapper.toSchema.name;
      return acc;
    },
    {} as {[id: string]: string}
  );
}

export function trackAnalytics(selectedItem: ProcessTask, isRoutingError: boolean): void {
  const rootTask = selectedItem.rootTask;
  assert(!isInaccessible(selectedItem.taskSchema), 'Inaccessible task schema in FFM analytics');
  analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.CORRECTED_FAILED_AUTOMATED_MAPPING, {
    process_client_origin: !isInaccessible(selectedItem.taskSchema)
      ? selectedItem.taskSchema.clientOrigin
      : null,
    output_api_id: null,
    task_api_id: selectedItem.id,
    task_group_api_id: selectedItem.process.id,
    flowchart_task_api_id: rootTask?.id ?? null,
    flowchart_task_group_api_id: rootTask?.process.id ?? null,
    task_schema_type: selectedItem.taskSchema.statusLifecycle.schemaExecutionType,
    is_routing_failure: isRoutingError,
  });
}
